import { createReducer } from '@reduxjs/toolkit'
import { some } from 'lodash'
import { types } from '../../actions/campaigns/locations_selector_actions'

const makeItemSelected = (state, itemId, selected) => {
  const itemIndex = state.locations.findIndex((item) => item.id == itemId)
  if (itemIndex !== -1) {
    state.locations[itemIndex] = { ...state.locations[itemIndex], selected }
  }
}

const makeAllSelected = (state, entityKey, selected) => {
  state[entityKey] = state[entityKey].map((item) => ({ ...item, selected }))
}

const selectAllLocations = (state) => {
  state.locations = state.locations.map((item) => {
    if (item.visible === true) {
      return { ...item, selected: true }
    }
    return item
  })
}

const locationIsVisible = (state, location, filtered_locations = null, tags = null) => {
  let filteredLocationIds = []
  if (filtered_locations !== null) {
    filteredLocationIds = filtered_locations.map((r) => r.id)
  }
  const tagIds =
    tags !== null
      ? tags.map((t) => t.value)
      : state.tags
          .filter((t) => t.selected)
          .map((t) => t.value)
  if (filteredLocationIds.length === 0 && tagIds.length === 0) {
    return true
  }

  const filteredLocationMatched =
    filteredLocationIds.length === 0 ? true : filteredLocationIds.includes(location.id)
  const tagsMatched = tagIds.length === 0 ? true : some(tagIds, (tagId) => location.tagIDs?.includes(tagId))

  return filteredLocationMatched && tagsMatched
}

const filterBy = (state, value) => {
  const filterValue = value.toLowerCase().trim()
  if (!filterValue) {
    state.locations.forEach((loc) => {
      loc.visible = true
    })
    return
  }

  const { locations, regions } = state

  const filteredLocations = locations.filter((location) => {
    return (
      location?.number?.toLowerCase().includes(filterValue) ||
      location.name?.toLowerCase().includes(filterValue) ||
      location.address?.zip?.toLowerCase().includes(filterValue) ||
      location.address?.city?.toLowerCase().includes(filterValue) ||
      location.address?.line1?.toLowerCase().includes(filterValue) ||
      location.address?.line2?.toLowerCase().includes(filterValue) ||
      location.address?.country?.toLowerCase().includes(filterValue)
    )
  })

  const filteredRegions = regions.filter((region) => {
    return region.label.toLowerCase().includes(filterValue)
  })

  const regionIds = filteredRegions.map((region) => region.value)
  const filteredByRegionLocations = locations.filter((location) => regionIds.includes(location.regionId))

  const combinedLocations = filteredLocations.concat(filteredByRegionLocations)
  const uniqueLocations = [...new Set(combinedLocations.map((location) => JSON.stringify(location)))].map(
    (stringified: string) => JSON.parse(stringified)
  )

  if (uniqueLocations.length === 0) {
    state.locations.forEach((loc) => {
      loc.visible = false
    })
    return
  }

  state.locations.forEach((loc) => {
    loc.visible = locationIsVisible(state, loc, uniqueLocations)
  })
}

const filterByTags = (state, tags) => {
  if (tags.length == 0) {
    state.locations.forEach((loc) => {
      loc.visible = locationIsVisible(state, loc, null, [])
    })
    state.tags.forEach((tag) => {
      tag.selected = false
    })
    return
  }

  const tagIds = tags.map((r) => r.value)
  state.locations.forEach((loc) => {
    loc.visible = locationIsVisible(state, loc, null, tags)
  })
  state.tags.forEach((tag) => {
    tag.selected = tagIds.includes(tag.value)
  })
}

const filterTargetBy = (state, value) => {
  const filterValue = value?.toLowerCase().trim() || ''
  state.targetFilter = filterValue
  
  if (!filterValue) {
    state.locations.forEach((loc) => {
      loc.targetVisible = true
    })
    return
  }

  const { locations } = state

  const filteredLocations = locations.filter((location) => {
    return (
      location?.number?.toLowerCase().includes(filterValue) ||
      location.name?.toLowerCase().includes(filterValue)
    )
  })

  if (filteredLocations.length === 0) {
    state.locations.forEach((loc) => {
      loc.targetVisible = false
    })
    return
  }

  const filteredLocationIds = filteredLocations.map((loc) => loc.id)
  state.locations.forEach((loc) => {
    loc.targetVisible = filteredLocationIds.includes(loc.id)
  })
}

const initBoxes = (state, action) => {
  const {
    locations,
    tags,
    id,
    step,
    regions,
    isReady,
    approvedLocationIDs,
    campaignLocationsNeedApproval,
    exportLocationApprovalsUrl,
    companyId,
    canCreateLocation,
    countries,
    locationManagers,
  } = action

  state.locations = (locations || []).map(location => ({
    ...location,
    targetVisible: true
  }))
  state.tags = tags || []
  state.id = id
  state.step = step
  state.regions = regions || []
  state.isReady = isReady
  state.approvedLocationIDs = approvedLocationIDs || []
  state.campaignLocationsNeedApproval = campaignLocationsNeedApproval
  state.exportLocationApprovalsUrl = exportLocationApprovalsUrl
  state.companyId = companyId
  state.canCreateLocation = canCreateLocation || false
  state.countries = countries || []
  state.locationManagers = locationManagers || []
}

const initialState = {
  locations: [],
  tags: [],
  id: null,
  step: null,
  regions: [],
  isReady: false,
  approvedLocationIDs: [],
  campaignLocationsNeedApproval: false,
  exportLocationApprovalsUrl: null,
  companyId: null,
  canCreateLocation: false,
  countries: [],
  locationManagers: [],
  createLocation: {
    loading: false,
    error: null,
    success: false
  }
}

export default createReducer(initialState, (builder) => {
  builder
    .addCase(types.INIT_BOXES, (state, action) => {
      initBoxes(state, action)
    })
    .addCase(types.FILTER_BY, (state, action) => {
      filterBy(state, action.value)
    })
    .addCase(types.FILTER_BY_TAGS, (state, action) => {
      filterByTags(state, action.tags)
    })
    .addCase(types.FILTER_TARGET_BY, (state, action) => {
      filterTargetBy(state, action.value)
    })
    .addCase(types.SELECT_ITEM, (state, action) => {
      makeItemSelected(state, action.itemId, true)
    })
    .addCase(types.SELECT_ALL, (state) => {
      selectAllLocations(state)
    })
    .addCase(types.REMOVE_ITEM, (state, action) => {
      makeItemSelected(state, action.itemId, false)
    })
    .addCase(types.REMOVE_ALL, (state) => {
      makeAllSelected(state, 'tags', false)
      makeAllSelected(state, 'locations', false)
    })
    .addCase(types.SAVE_SELECTED, (state) => {
      // No-op
    })
    .addCase(`${types.CREATE_LOCATION}_REQUEST` as any, (state) => {
      state.createLocation.loading = true
      state.createLocation.error = null
      state.createLocation.success = false
    })
    .addCase(types.CREATE_LOCATION, (state, action: any) => {
      if (action.data && action.data.location) {
        const newLocation = action.data.location
        // Transform the location to match the expected format with region_name
        const formattedLocation = {
          id: newLocation.id,
          number: newLocation.number,
          name: newLocation.name,
          address: newLocation.address,
          selected: true,  // Automatically select the newly created location
          visible: true,
          targetVisible: true,
          regionId: newLocation.region_id,
          tagIDs: newLocation.tag_ids || [],
          regionName: newLocation.region_name || ''
        }
        state.locations.push(formattedLocation)
      }
      state.createLocation.loading = false
      state.createLocation.success = true
      state.createLocation.error = null
    })
    .addCase(`${types.CREATE_LOCATION}_FAILURE` as any, (state, action: any) => {
      state.createLocation.loading = false
      state.createLocation.error = action.error?.message || 'Failed to create location'
      state.createLocation.success = false
    })
    .addCase(types.RESET_CREATE_LOCATION, (state) => {
      state.createLocation.loading = false
      state.createLocation.error = null
      state.createLocation.success = false
    })
})

